import { createContext, useContext, ReactNode, useEffect } from "react";
import {
  useRegion,
  Region,
  formatPrice,
  convertPrice,
  convertOriginalPrice,
  regionToCurrency,
  CURRENCY_CONFIG,
} from "@/hooks/use-region";
import { formatAmount, type CurrencyCode } from "@/config/pricing";
import { useLanguage } from "@/contexts/LanguageContext";

interface RegionContextType {
  region: Region;
  loading: boolean;
  setRegion: (r: Region) => void;
  currency: (typeof CURRENCY_CONFIG)[Region];
  currencyCode: CurrencyCode;
  formatPrice: (egpAmount: number) => string;
  formatOriginalPrice: (egpAmount: number) => string | null;
  convertPrice: (egpAmount: number) => number;
  isGCC: boolean;
  isEgypt: boolean;
  isInternational: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const { region, loading, setRegion } = useRegion();
  const { lang, toggleLanguage, setRegionDialect } = useLanguage();

  useEffect(() => {
    if (!loading) setRegionDialect(region);
  }, [region, loading, setRegionDialect]);

  useEffect(() => {
    if (loading) return;
    const hasVisited = localStorage.getItem("has_visited");
    if (hasVisited) return;
    // First visit: auto-switch to English for non-Arabic / international visitors.
    if (region === "other" && lang === "ar") toggleLanguage();
    localStorage.setItem("has_visited", "true");
  }, [region, loading]);

  const currencyCode = regionToCurrency(region);
  const isEgypt = region === "eg";

  const value: RegionContextType = {
    region,
    loading,
    setRegion: (r: Region) => {
      setRegion(r);
      setRegionDialect(r);
    },
    currency: CURRENCY_CONFIG[region],
    currencyCode,
    formatPrice: (egp) => formatPrice(egp, region, lang),
    formatOriginalPrice: (egp) => {
      const v = convertOriginalPrice(egp, region);
      return v ? formatAmount(v, currencyCode, lang) : null;
    },
    convertPrice: (egp) => convertPrice(egp, region),
    isGCC: region === "sa",
    isEgypt,
    isInternational: !isEgypt,
  };

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegionContext = () => {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error("useRegionContext must be used within RegionProvider");
  return ctx;
};
